import {
    IconButton,
    Paper,
    TextField,
    Typography
} from '@material-ui/core';
import {
    createStyles,
    WithStyles,
    withStyles
} from '@material-ui/core/styles';
import { Theme } from '@material-ui/core/styles';
import * as React from 'react';
import { CCSpinner } from '../../shared/components/cc-spinner';
import { PageBoundary } from '../../shared/components/simple-grid-pagination';
import { SimpleListItem } from '../../shared/components/simple-list';
import { SimpleListPagination } from '../../shared/components/simple-list-pagination';
import { Team } from '../../shared/domain';
import { ClearOutlined, SearchOutlined } from '@material-ui/icons';
import { AccountFull } from '../../models/account';

const styles = (theme: Theme) => createStyles({
    [theme.breakpoints.down('lg')]: {
        simpleListTeams: {
            width: '26em !important'
        },
    },
    root: {
        display: 'flex',
        flexWrap: 'wrap',
    },
    container: {
        display: 'flex',
        flexDirection: 'column'
    },
    heading: {
        fontWeight: 700,
        marginBottom: '0.5em'
    },
    ellipsis: {
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        overflow: 'hidden'
    },
    simpleList: {
        overflow: 'auto',
        height: '100%',
        minWidth: '20em',
        maxWidth: '25em'
    },
    simpleListAccounts: {
        marginRight: '1.5em',
        width:'25em'
    },
    simpleListTeams: {
        width: '35em'
    },
    teamSpinnerContainer: {
        alignItems: 'center',
        justifyContent: 'center',
        marginTop: '3em',
        marginBottom: '4em',
        borderRadius: '0.4em'
    },
    noAccountContainer: {
        fontWeight: 'bold',
         '& > *': {
            color: 'rgba(0,0,0,0.2)'
        }
    },
    searchInputContainer: {
        marginBottom: '1em'
    },
    searchInput: {
        width: '100%',
        paddingLeft: '1em'
    },
});

interface Props extends WithStyles<typeof styles> {
    accounts?: AccountFull[] | null;
    accountsIsLoading: boolean;
    currentAccountName?: string;
    teams?: Team[] | null;
    teamSelection: boolean;
    teamsIsLoading: boolean;
    teamsItemsOffset: number;
    teamsTotalItems?: number;
    accountsTotalItems?: number;
    accountsItemsOffset: number;
    rowsPerPage: number;
    onAccountClick?: (account: AccountFull) => void ;
    onAccountDataPageOverBoundary?: (boundary: PageBoundary, nextPage: number) => Promise<void>;
    onTeamClick?: (team: Team) => void;
    onTeamDataPageOverBoundary?: (boundary: PageBoundary, nextPage: number) => Promise<void>;
    onAccountSearch?: (searchName: string) => void;
};

interface States {
    teamsIsLoading: boolean;
    accountNameSearch: string;
};

class AccountTeamViewer extends React.Component<Props, States> {
    public static defaultProps = {
        currentAccountName: '',
        accountsItemsOffset: 0,
        teamsItemsOffset: 0,
        teamSelection: true
    };

    state = {
        teamsIsLoading: false,
        accountNameSearch: ''
    };

    onAccountClicked = (listItem: SimpleListItem) => {
        const {accounts, onAccountClick} = this.props;
        if(!accounts) {
            return;
        }

        const currentAccount = accounts.find((account:AccountFull) => {
            return account.account_id === listItem.id;
        });
        if (!currentAccount) {
            return;
        }

        if (onAccountClick) {
            onAccountClick(currentAccount);
        }
    };

    onTeamClicked = (listItem: SimpleListItem) => {
        const {teams, onTeamClick} = this.props;
        if(!teams) {
            return;
        }

        const currentTeam = teams.find((team: Team) => {
            return team.team_id === listItem.id;
        });
        if (!currentTeam) {
            return;
        }

        if (onTeamClick) {
            onTeamClick(currentTeam);
        }
    };

    onAccountNameSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        const searchValue = event.target.value;

        this.setState({
            accountNameSearch: searchValue
        });
    };

    onAccountNameKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        const { accountNameSearch } = this.state;
        const { onAccountSearch } = this.props;

        if (!onAccountSearch) {
            return;
        }

        if (event.key === 'Delete' || event.key === 'Backspace') {
            if (accountNameSearch === '') {
                onAccountSearch('');
                return;
            }
        }

        if (event.key === 'Enter') {
            onAccountSearch(accountNameSearch);
            return;
        }
    };

    onButtonSearch = () => {
        const { accountNameSearch } = this.state;
        const { onAccountSearch } = this.props;

        if (!onAccountSearch) {
            return;
        }

        onAccountSearch(accountNameSearch);
    };

    onCancelSearch = () => {
        const { onAccountSearch } = this.props;

        if (!onAccountSearch) {
            return;
        }

        this.setState({ accountNameSearch: '' }, () => {
            onAccountSearch('');
        });
    };

    public render() {
        const {
            classes,
            accounts,
            currentAccountName,
            accountsIsLoading,
            teams,
            teamsItemsOffset,
            teamSelection,
            teamsIsLoading,
            teamsTotalItems,
            accountsTotalItems,
            accountsItemsOffset,
            rowsPerPage,
            onAccountDataPageOverBoundary,
            onTeamDataPageOverBoundary
        } = this.props;

        const {
            accountNameSearch
        } = this.state;

        const accountListItems = accounts ? accounts.map((account: AccountFull) => {
            const accountId = account.account_id;
            return new SimpleListItem(accountId, account.name, accountId);
        }) : [];
        const teamListItems = teams ? teams.map(((team, index) => {
            const teamId = team.team_id;
            return new SimpleListItem(teamId, team.name, teamId);
        })) : [];

        return (
            <div className={classes.root} data-testid="account-team-viewer">
                <div className={`${classes.container} ${classes.simpleListAccounts}`}>
                    <Typography
                        variant='h6'
                        className={`${classes.heading} ${classes.ellipsis}`}
                    >
                        Customer Organizations
                    </Typography>
                    <Paper className={classes.searchInputContainer}>
                        <TextField
                            data-testid="account-searchbar"
                            className={classes.searchInput}
                            type="text"
                            placeholder="Search by name"
                            variant="standard"
                            multiline={false}
                            value={accountNameSearch}
                            InputProps={{
                                disableUnderline: true,
                                endAdornment: (
                                    <React.Fragment>
                                        {
                                            accountNameSearch !== '' &&
                                                <IconButton
                                                    aria-label="cancel"
                                                    data-testid="cancel-search"
                                                    onClick={this.onCancelSearch}
                                                >
                                                    <ClearOutlined />
                                                </IconButton>
                                        }
                                        <IconButton
                                            aria-label="search"
                                            data-testid="commit-search"
                                            onClick={this.onButtonSearch}
                                        >
                                            <SearchOutlined />
                                        </IconButton>
                                    </React.Fragment>
                                )
                            }}
                            onChange={this.onAccountNameSearch}
                            onKeyDown={this.onAccountNameKeyDown}
                        />
                    </Paper>
                    <SimpleListPagination
                        className={classes.simpleList}
                        keepItemSelected={true}
                        items={accountListItems}
                        isLoading={accountsIsLoading}
                        onListItemClick={this.onAccountClicked}
                        rowsPerPage={rowsPerPage}
                        totalItems={accountsTotalItems}
                        offset={accountsItemsOffset}
                        onPageOverBoundary={onAccountDataPageOverBoundary}
                        noItemsLabel='No accounts found'
                    />
                </div>
                { teams && (teams.length > 0) ?
                        <div className={`${classes.container} ${classes.simpleListTeams}`}>
                            <Typography
                                variant='h6'
                                className={`${classes.heading} ${classes.ellipsis}`}
                            >
                                Teams on {currentAccountName}
                            </Typography>
                                <SimpleListPagination
                                    className={classes.simpleList}
                                    disabled={!teamSelection}
                                    isLoading={teamsIsLoading}
                                    items={teamListItems}
                                    offset={teamsItemsOffset}
                                    onListItemClick={this.onTeamClicked}
                                    onPageOverBoundary={onTeamDataPageOverBoundary}
                                    rowsPerPage={rowsPerPage}
                                    totalItems={teamsTotalItems}
                                />
                        </div>
                        :
                        <Paper className={`${classes.container} ${classes.simpleListTeams} ${classes.teamSpinnerContainer} ${(!teamsIsLoading ? classes.noAccountContainer : '')}`}>
                            <CCSpinner 
                                loading={teamsIsLoading}
                                size={70}
                            >
                                <Typography variant={!teams ? 'h4' : 'h5'}>
                                    {
                                        !teams ? 'No organization selected' : `No teams for ${currentAccountName ? currentAccountName : 'the selected account'}`
                                    }
                                </Typography>
                            </CCSpinner>
                        </Paper>
                }
            </div>
        );
    }
}

const MUIComponent = withStyles(styles)(AccountTeamViewer);
export {MUIComponent as AccountTeamViewer}

